/**
 * PostBookingConnections — Trust-Drip component
 *
 * Shown AFTER a booking/payment is confirmed. Asks user to connect
 * calendar, email, etc. — only after value has been proven.
 */
import React from 'react';

interface ConnectionService {
  provider?: string;
  label?: string;
  reason?: string;
  action?: string;
}

interface PostBookingConnectionsProps {
  message: string;
  services: (string | ConnectionService)[];
  onConnect: (service: string) => void;
  onDismiss: () => void;
}

const SERVICE_INFO: Record<string, { label: string; icon: string; desc: string }> = {
  google_calendar: {
    label: 'Google Calendar',
    icon: '📅',
    desc: 'Add trip dates, check-in times, and reminders',
  },
  gmail: {
    label: 'Gmail',
    icon: '✉️',
    desc: 'Forward booking confirmations and receipts',
  },
};

export const PostBookingConnections: React.FC<PostBookingConnectionsProps> = ({
  message,
  services,
  onConnect,
  onDismiss,
}) => {
  const normalizedServices = services.map(s => {
    if (typeof s === 'string') {
      const info = SERVICE_INFO[s] || { label: s, icon: '🔗', desc: '' };
      return { id: s, ...info };
    }
    const info = SERVICE_INFO[s.provider || ''] || { label: s.label || s.provider || '', icon: '🔗', desc: s.reason || '' };
    return { id: s.provider || s.label || '', ...info, desc: s.reason || info.desc };
  });

  return (
    <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-cyan-900/20 to-emerald-900/20 border border-cyan-400/20">
      <p className="text-sm text-cyan-100 mb-3">{message}</p>
      <div className="flex flex-col gap-2">
        {normalizedServices.map(svc => (
          <button
            key={svc.id}
            onClick={() => onConnect(svc.id)}
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-black/30 border border-cyan-400/20 hover:border-cyan-400/50 hover:bg-cyan-900/20 transition-all text-left group"
          >
            <span className="text-xl">{svc.icon}</span>
            <div className="flex-1">
              <div className="text-sm font-medium text-cyan-300 group-hover:text-cyan-200">{svc.label}</div>
              {svc.desc && <div className="text-xs text-cyan-400/60">{svc.desc}</div>}
            </div>
            <span className="text-xs text-cyan-400/50 group-hover:text-cyan-300">Connect →</span>
          </button>
        ))}
      </div>
      <button
        onClick={onDismiss}
        className="mt-2 text-xs text-cyan-400/40 hover:text-cyan-400/70 transition-colors"
      >
        Maybe later
      </button>
    </div>
  );
};

export default PostBookingConnections;
